// eslint-disable-next-line no-restricted-imports
import { ThemeProvider } from '@emotion/react';
import { useCartDrawer } from '@iheartjane/cart';
import { saveEntryParams } from '@iheartjane/cart/utils';
import { sendMfaCode, verifyMfaCode } from '@iheartjane/data-access';
import { useInProgressReservations } from '@iheartjane/data-access/react-query';
import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import { MfaProvider } from '@iheartjane/menu';
import {
  Box,
  Flex,
  ReeferThemeProvider,
  ToastProvider,
  generateTheme,
  generateThemeCSS,
  shadowAppPortal,
  shadowRoot,
  useMobileMediaQuery,
} from '@iheartjane/reefer';
import { useRuntimeConfig } from '@iheartjane/runtime-config';
import { useEffect, useMemo } from 'react';
import { Outlet, useLocation } from 'react-router';

import { SDKProvider } from '@jane/dm/internal';
import { ApplicationGates } from '@jane/monolith/components/applicationGates/applicationGates';
import { useCartState } from '@jane/monolith/components/cart/cartFacade/useCartState';
import { PendingCartDrawer } from '@jane/monolith/components/cart/pendingCartDrawer/pendingCartDrawer';
import { CheckAuthTimer } from '@jane/monolith/components/checkAuthTimer';
import { ServerNotifications } from '@jane/monolith/components/notifications/serverNotifications';
import { SetJaneDeviceId } from '@jane/monolith/components/SetJaneDeviceId';
import { SkipLinks } from '@jane/monolith/components/skipLinks';
import { useCustomerDispatch } from '@jane/monolith/customer/dispatch';
import { applyLoggedInUser } from '@jane/monolith/customer/redux/customer';
import {
  selectEmbeddedApp,
  setPartner,
  verifyStore,
} from '@jane/monolith/customer/redux/embeddedApp';
import { selectStore } from '@jane/monolith/customer/redux/store';
import { useCustomerSelector } from '@jane/monolith/customer/selectors';
import { useAuth } from '@jane/monolith/hooks/useAuth/useAuth';
import { useEmbeddedOptionsListener } from '@jane/monolith/hooks/useEmbeddedOptionsListener';
import { useForceClientRefresh } from '@jane/monolith/hooks/useForceClientRefresh';
import { usePrivacyMode } from '@jane/monolith/hooks/usePrivacyMode';
import { IdentifyCustomer } from '@jane/monolith/tracking/identifyCustomer';
import { MenuHeaderEmbed } from '@jane/shared-ecomm/components';
import { useCurrentStoreId } from '@jane/shared-ecomm/hooks';
import {
  AuthenticationProvider,
  BrandSpecialsProvider,
  UserPreferencesProvider,
} from '@jane/shared-ecomm/providers';
import { trackCartClick } from '@jane/shared-ecomm/tracking';
import { getStoreTheme } from '@jane/shared-ecomm/util';
import { getJwt } from '@jane/shared/auth';
import { LoadingWrapper, MfaModal } from '@jane/shared/components';
import { useJaneUser } from '@jane/shared/data-access';
import { useEcommApp } from '@jane/shared/providers';
import { getEcommPaths, parseSearch } from '@jane/shared/util';

import { StoreScripts } from './storeScripts';

export const FramelessEmbedAppContainer = () => {
  useForceClientRefresh();
  const dispatch = useCustomerDispatch();
  const { open: openCartDrawer } = useCartDrawer();
  const location = useLocation();
  const { appMode } = useEcommApp();
  const initialStoreId = useCurrentStoreId();
  const authentication = useAuth({ initialStoreId });
  const isMobile = useMobileMediaQuery({});
  const { theme } = useEmbeddedOptionsListener();
  const { isPartnerHosted, partnerHostedPath, isBoostForAll } =
    useRuntimeConfig();

  const { isLoadingStore, store } = useCustomerSelector(selectStore);
  const { disableAuthFeatures, isVerifying, partnerId } =
    useCustomerSelector(selectEmbeddedApp);
  const { cart, cartSize } = useCartState();
  const privacyModeEnabled = useFlag(FLAGS.p1HeadlessPrivacyMode, false);
  usePrivacyMode(privacyModeEnabled);

  const cartProducts = cart?.products ?? [];

  const storePathProps = {
    id: store?.id ?? '',
    name: store?.name ?? '',
    slug: store?.url_slug ?? '',
  };

  const ecommPaths = getEcommPaths(
    {
      appMode: 'framelessEmbed',
      isBoostForAll,
      isPartnerHosted,
      partnerHostedPath,
      storePathProps,
    },
    location.search
  );

  const {
    data: janeUser,
    isPending: isLoadingUser,
    isRefetching: isRefetchingUser,
  } = useJaneUser();
  const authenticated = janeUser?.user?.authenticated;

  const { data: inProgressReservations } = useInProgressReservations({
    enabled: !isRefetchingUser && authenticated,
    jwt: getJwt(),
    storeId: store?.id,
  });

  // TODO: Remove this once we fully remove the customer redux state
  useEffect(() => {
    if (janeUser?.user) {
      dispatch(applyLoggedInUser(janeUser?.user));
    }
  }, [janeUser?.user]);

  useEffect(() => {
    const query = parseSearch<Record<string, string | undefined>>(
      window.location.search
    );
    saveEntryParams(appMode, initialStoreId, query);

    dispatch(setPartner({ id: initialStoreId, name: '' }));
    dispatch(
      verifyStore({
        storeId: initialStoreId!,
        options: {
          forceEmbed: !!query['embed'],
          isPartnerHosted,
          framelessEmbed: true,
        },
      })
    );
    const body = document.querySelector('body');
    if (body) {
      body.classList.add('embedded'); // set fixed modal height for embedded/whiteLabel. see layout.css
    }
  }, []);

  const onCartClick = () => {
    openCartDrawer();
    trackCartClick();
  };

  const isLoading = isLoadingStore || isVerifying || isLoadingUser;
  const shadowHost = shadowRoot();
  const drawerContainer = shadowAppPortal() || undefined;

  // Inject reefer theme CSS into the shadow root instead of document.head
  // so it doesn't leak into the partner site's light DOM.
  const themeConfig = useMemo(
    () => theme || getStoreTheme(store),
    [theme, store]
  );

  useEffect(() => {
    if (!shadowHost) return;

    const STYLE_TAG_ID = 'reefer-theme-css';
    let styleTag = shadowHost.getElementById(STYLE_TAG_ID);

    if (!styleTag) {
      styleTag = document.createElement('style');
      styleTag.id = STYLE_TAG_ID;
      shadowHost.appendChild(styleTag);
    }

    const baseTheme = generateTheme(themeConfig);
    const themeCSS = generateThemeCSS(baseTheme);
    // generateThemeCSS outputs a `:root { ... }` block. Inside a shadow DOM,
    // CSS variables must be on `:host` to scope correctly, so we swap the selector.
    // If generateThemeCSS's output format changes, this replace must be updated.
    styleTag.textContent = themeCSS.replace(':root {', ':host {');
  }, [shadowHost, themeConfig]);

  return (
    <LoadingWrapper isLoading={isLoading} variant="full-page">
      <StoreScripts storeId={partnerId} />
      <CheckAuthTimer />
      <SetJaneDeviceId />

      <IdentifyCustomer
        app="framelessEmbed"
        appStoreId={initialStoreId}
        /** @todo possible to replace with useLocation hook? */
        /* eslint-disable-next-line @iheartjane/workspace/no-dom-globals-in-react-fc */
        queryParams={parseSearch(window.location.search)}
      />
      <ReeferThemeProvider
        loadFonts={false}
        mode="server"
        renderProvider={(theme, children) => (
          <ThemeProvider theme={theme}>{children}</ThemeProvider>
        )}
        theme={themeConfig}
      >
        <ToastProvider position={isMobile ? 'top-middle' : 'top-right'}>
          <MfaProvider
            getJwt={() => getJwt()}
            sendMfaCode={sendMfaCode}
            verifyMfaCode={verifyMfaCode}
            userId={janeUser?.user?.id ?? null}
          >
            <UserPreferencesProvider>
              <AuthenticationProvider value={{ ...authentication }}>
                <BrandSpecialsProvider
                  brandSpecialHasChanged={cart?.brand_special_has_changed}
                  cartProducts={cartProducts}
                  storeId={initialStoreId}
                >
                  <SDKProvider>
                    <PendingCartDrawer container={drawerContainer} />
                    <MfaModal />
                    <SkipLinks />
                    {/*TODO: can ReeferThemeProvider get moved to the top level?*/}
                    <ApplicationGates />

                    <ServerNotifications />
                    <Flex pb={24} flexDirection="column" grow>
                      <MenuHeaderEmbed
                        py={24}
                        disableAuthFeatures={disableAuthFeatures}
                        inProgressReservations={inProgressReservations?.length}
                        onCartClick={onCartClick}
                        paths={ecommPaths}
                        productsInCart={cartSize}
                        store={store}
                      />

                      <Box>
                        <LoadingWrapper isLoading={isLoading}>
                          <Outlet />
                        </LoadingWrapper>
                      </Box>
                    </Flex>
                  </SDKProvider>
                </BrandSpecialsProvider>
              </AuthenticationProvider>
            </UserPreferencesProvider>
          </MfaProvider>
        </ToastProvider>
      </ReeferThemeProvider>
    </LoadingWrapper>
  );
};
